import React, { useCallback, useMemo, useRef, useState, } from "../../__compat__/react/main.entry.mjs.mjs";
import { addYears, subYears } from "../../__compat__/date-fns/main.entry.mjs.mjs";
import { useMedia } from "../../hooks/useMedia.mjs.mjs";
import { useStyledTheme } from "../../hooks/index.mjs.mjs";
import { useLocale } from "../LocaleProvider/index.mjs.mjs";
import { Popover, PopoverBody, PopoverTrigger } from "../Popover/index.mjs.mjs";
import { DateInput, DateInputExtension } from "./DateInput/index.mjs.mjs";
import { useCalendarFocus, useCalendarSelection, useCalendarTabIndex, } from "./Calendar/index.mjs.mjs";
import { SingleCalendar } from "./SingleCalendar/index.mjs.mjs";
import { StyledCalendarContainer } from "./DatePicker.styles.mjs.mjs";
import { DateSummary } from "./DateSummary/index.mjs.mjs";
import { MobileOverlay } from "./MobileOverlay/index.mjs.mjs";
import { useCalendarMinMax } from "./Calendar/useCalendarMinMax.mjs.mjs";
import { resolveDateInRange } from "./dateHelpers.mjs.mjs";
const noop = () => { };
const ExtensionWithPopover = ({ children, onClose, autoClose, targetRef, ...extentionProps }) => {
    const { datePicker: locale } = useLocale();
    return (React.createElement(PopoverTrigger, { targetRef: targetRef, onClose: onClose }, (triggerProps) => {
        return (React.createElement(React.Fragment, null,
            React.createElement(DateInputExtension, { "aria-label": locale.openCalendarLabel, ...extentionProps, ...triggerProps }),
            React.createElement(Popover, { "aria-label": locale.calendarOverlayLabel },
                React.createElement(PopoverBody, null, !autoClose
                    ? children
                    : React.Children.map(children, (child) => child &&
                        React.cloneElement(child, {
                            onSelection: () => {
                                var _a;
                                const element = triggerProps.ref;
                                (_a = element === null || element === void 0 ? void 0 : element.current) === null || _a === void 0 ? void 0 : _a.click();
                            },
                        }))))));
    }));
};
const ExtensionWithMobileOverlay = ({ children, unconfirmedValue, onClose, onConfirm, onExit, ...extentionProps }) => {
    const { datePicker: locale } = useLocale();
    const [pickerOpen, setPickerOpen] = useState(false);
    const handleTogglePickerOpen = useCallback((open) => {
        setPickerOpen(open);
        if (!open) {
            onClose();
        }
    }, [onClose]);
    return (React.createElement(React.Fragment, null,
        React.createElement(DateInputExtension, { onClick: () => {
                handleTogglePickerOpen(!pickerOpen);
            }, "aria-label": locale.openCalendarLabel, ...extentionProps }),
        React.createElement(MobileOverlay, { onClose: () => {
                onExit();
                handleTogglePickerOpen(false);
            }, onConfirm: () => {
                onConfirm();
                handleTogglePickerOpen(false);
            }, open: pickerOpen },
            React.createElement(DateSummary, { value: unconfirmedValue !== null && unconfirmedValue !== void 0 ? unconfirmedValue : new Date() }),
            React.createElement(StyledCalendarContainer, null, children))));
};
export const DatePicker = ({ minDate = subYears(new Date(), 100), maxDate = addYears(new Date(), 100), value = null, onChange = noop, onClose = noop, id, closeAfterSelection, ...fieldProps }) => {
    const [unconfirmedValue, setUnconfirmedValue] = useState(value);
    const inputRef = useRef(null);
    const { breakpoints } = useStyledTheme();
    const isDesktop = useMedia(`(min-width: ${breakpoints.m}px)`);
    const baseId = id || `${fieldProps.inputId}-date-picker`;
    const interval = useMemo(() => ({
        start: minDate,
        end: maxDate,
    }), [maxDate, minDate]);
    const [currentMonth, setCurrentMonth] = useState(resolveDateInRange(value, interval));
    const { handleFocus, getFocusProps } = useCalendarFocus();
    const updateCurrentMonth = useCallback((nextDate) => {
        setCurrentMonth(nextDate || new Date());
    }, []);
    const handleDateSelect = useCallback((nextDate) => {
        const singleDate = (Array.isArray(nextDate) ? nextDate : [nextDate])[0];
        if (isDesktop) {
            onChange(singleDate);
        }
        else {
            setUnconfirmedValue(singleDate);
        }
        updateCurrentMonth(singleDate);
    }, [onChange, isDesktop, setUnconfirmedValue, updateCurrentMonth]);
    const handleInputChange = useCallback((nextValue, event) => {
        onChange(nextValue, event);
        setUnconfirmedValue(nextValue);
        updateCurrentMonth(nextValue);
    }, [onChange, updateCurrentMonth]);
    const { handleSelection, getSelectionProps } = useCalendarSelection(isDesktop ? value : unconfirmedValue, handleDateSelect);
    const { getTabIndexProp } = useCalendarTabIndex(value);
    const { getMinMaxProps } = useCalendarMinMax(minDate, maxDate);
    const getDateProps = (date) => ({
        ...getFocusProps(date),
        ...getSelectionProps(date),
        ...getTabIndexProp(date),
        ...getMinMaxProps(date),
    });
    const calendarProps = {
        autoFocusSelected: true,
        id: `${baseId}-calendar`,
        minDate,
        maxDate,
        month: currentMonth,
        closeAfterSelection,
        onMonthChange: setCurrentMonth,
        onClickDate: handleSelection,
        onKeyDownDate: handleFocus,
        onSelection: noop,
        getDateProps,
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(DateInput, { inputRef: inputRef, value: value, onChange: handleInputChange, ...fieldProps, ...(fieldProps.readOnly
                ? {}
                : {
                    extensionEnd: isDesktop ? (React.createElement(ExtensionWithPopover, { onClose: onClose, targetRef: inputRef, autoClose: closeAfterSelection },
                        React.createElement(SingleCalendar, { ...calendarProps }))) : (React.createElement(ExtensionWithMobileOverlay, { onClose: onClose, unconfirmedValue: unconfirmedValue, onConfirm: () => {
                            onChange(unconfirmedValue);
                        }, onExit: () => {
                            setUnconfirmedValue(value);
                        } },
                        React.createElement(SingleCalendar, { ...calendarProps }))),
                }) })));
};
//# sourceMappingURL=DatePicker.mjs.mjs.map